import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  isImportActive,
  sameImportStatusApartFromClock,
  type ExternalEmrImportStatus,
} from '../services/externalEmrImportStatus';

/**
 * A keyed module store for External EMR import statuses, with one fetch and
 * one poll per key no matter how many surfaces read it.
 *
 * Two owners use it: the intake (keyed by intake id) and the Patient Manager
 * cards and chart page (keyed by patient id). It is not TanStack Query on
 * purpose: these surfaces render in many existing tests without a
 * QueryClientProvider, and a provider requirement would break all of them.
 *
 * Polling runs only while someone is watching and the import can still
 * change (queued or running). It stops on its own once the import settles.
 */
export interface ImportSnapshot<S extends ExternalEmrImportStatus> {
  status: S;
  /** A first read has settled (successfully or not). */
  loaded: boolean;
}

interface Entry<S extends ExternalEmrImportStatus> {
  snapshot: ImportSnapshot<S>;
  /**
   * When this key last took a status from a start, cancel or status read
   * (ms). A list seed whose request began before this is older news.
   */
  updatedAt: number;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setTimeout> | null;
  inflight: Promise<void> | null;
}

export interface ImportStatusStoreOptions<S extends ExternalEmrImportStatus> {
  /** Reads the status for one key. Throws on failure. */
  fetch: (key: string) => Promise<S>;
  /** The empty status for a key nobody has read yet. */
  empty: S;
  /** Delay before the next read of an active import. */
  pollIntervalMs: (status: S) => number;
  /**
   * Whether this status can still change. Default: the import is queued or
   * running. The patient store also waits on print renders.
   */
  shouldPoll?: (status: S) => boolean;
  /** What to log when a read fails. */
  failureLog: string;
}

export interface ImportStatusStore<S extends ExternalEmrImportStatus> {
  /** Replace a key's status (after a start, cancel or list read). */
  publish: (key: string, status: S) => void;
  /** Re-read now, outside the poll. */
  refresh: (key: string) => Promise<void>;
  /** The key's current status, or null before anything was read or seeded. */
  get: (key: string) => S | null;
  /** Tests only: forget every key and stop every timer. */
  reset: () => void;
  /** React hook: the key's snapshot, polled while active. */
  useStatus: (
    key: string | null | undefined,
    options?: { enabled?: boolean; seed?: S | null; seedAt?: number | null }
  ) => ImportSnapshot<S> & { refresh: () => Promise<void> };
}

export function createImportStatusStore<S extends ExternalEmrImportStatus>({
  fetch,
  empty,
  pollIntervalMs,
  shouldPoll = status => isImportActive(status.state),
  failureLog,
}: ImportStatusStoreOptions<S>): ImportStatusStore<S> {
  const unloaded: ImportSnapshot<S> = { status: empty, loaded: false };
  const entries = new Map<string, Entry<S>>();

  function entryFor(key: string): Entry<S> {
    let entry = entries.get(key);
    if (!entry) {
      entry = {
        snapshot: unloaded,
        updatedAt: 0,
        listeners: new Set(),
        timer: null,
        inflight: null,
      };
      entries.set(key, entry);
    }
    return entry;
  }

  function clearTimer(entry: Entry<S>) {
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
  }

  function schedulePoll(key: string) {
    const entry = entryFor(key);
    clearTimer(entry);
    if (entry.listeners.size === 0) return;
    if (!shouldPoll(entry.snapshot.status)) return;
    entry.timer = setTimeout(() => {
      entry.timer = null;
      void read(key);
    }, pollIntervalMs(entry.snapshot.status));
  }

  function setSnapshot(
    key: string,
    snapshot: ImportSnapshot<S>,
    { fromSeed = false }: { fromSeed?: boolean } = {}
  ) {
    const entry = entryFor(key);
    entry.snapshot = snapshot;
    if (!fromSeed) entry.updatedAt = Date.now();
    entry.listeners.forEach(listener => listener());
    schedulePoll(key);
  }

  function read(key: string): Promise<void> {
    const entry = entryFor(key);
    if (entry.inflight) return entry.inflight;
    entry.inflight = (async () => {
      try {
        const status = await fetch(key);
        setSnapshot(key, { status, loaded: true });
      } catch (error) {
        // Silent by design: an unreadable status leaves the page usable, and a
        // toast every few seconds would bury it. Keep the last status so a
        // running import keeps polling through one blip.
        console.error(failureLog, error);
        setSnapshot(key, { status: entry.snapshot.status, loaded: true });
      } finally {
        entry.inflight = null;
      }
    })();
    return entry.inflight;
  }

  function subscribe(key: string, listener: () => void): () => void {
    const entry = entryFor(key);
    entry.listeners.add(listener);
    return () => {
      entry.listeners.delete(listener);
      if (entry.listeners.size === 0) clearTimer(entry);
    };
  }

  function useStatus(
    key: string | null | undefined,
    {
      enabled = true,
      seed = null,
      seedAt = null,
    }: { enabled?: boolean; seed?: S | null; seedAt?: number | null } = {}
  ) {
    const active = enabled && !!key;
    const k = key ?? '';

    const snapshot = useSyncExternalStore(
      useCallback(
        (listener: () => void) =>
          active ? subscribe(k, listener) : () => undefined,
        [active, k]
      ),
      () => (active ? entryFor(k).snapshot : unloaded)
    );

    // A list read is a fresh status, unless a read is in flight or this key
    // took a newer status (a start, cancel or poll) after the list request
    // began. Without that check, a list response that was already on its way
    // when the user clicked Import would reset the queued card to Ready.
    useEffect(() => {
      if (!active || !seed) return;
      const entry = entryFor(k);
      if (entry.inflight) return;
      if (seedAt != null && seedAt < entry.updatedAt) return;
      // List rows carry no stage or step; only the status read does. A seed
      // in the same state keeps the stage and step the poll already found,
      // all four together, so a list reload does not blank the step.
      const current = entry.snapshot.status;
      const next =
        entry.snapshot.loaded &&
        current.state === seed.state &&
        !seed.stage &&
        current.stage
          ? {
              ...seed,
              stage: current.stage,
              phase: current.phase ?? null,
              phase_index: current.phase_index ?? null,
              phase_count: current.phase_count ?? null,
            }
          : seed;
      // Same words as what is shown: nothing to publish (and no render loop).
      // Only the read time differs on an unchanged reload. Publishing it would
      // restart the poll timer, and reloads under the poll interval apart
      // would keep postponing the read that finds the next step.
      if (
        entry.snapshot.loaded &&
        sameImportStatusApartFromClock(current, next)
      ) {
        return;
      }
      setSnapshot(k, { status: next, loaded: true }, { fromSeed: true });
    }, [active, k, seed, seedAt]);

    useEffect(() => {
      if (!active) return;
      const entry = entryFor(k);
      if (!entry.snapshot.loaded) {
        if (!seed) void read(k);
      } else {
        schedulePoll(k);
      }
      // `seed` is handled by the effect above; depending on it here would
      // refetch on every list reload.
    }, [active, k]);

    const refresh = useCallback(
      () => (active ? read(k) : Promise.resolve()),
      [active, k]
    );

    return { ...snapshot, refresh };
  }

  return {
    publish: (key, status) => setSnapshot(key, { status, loaded: true }),
    refresh: read,
    get: key => {
      const entry = entries.get(key);
      return entry?.snapshot.loaded ? entry.snapshot.status : null;
    },
    reset: () => {
      entries.forEach(clearTimer);
      entries.clear();
    },
    useStatus,
  };
}
